/**
 * Panel de pago compartido de Mi Cuenta.
 *
 * Un solo Payment Element para todo lo que se cobra en la sección:
 * suscripción, paquete de timbres, paquete de créditos de IA, Premium
 * prepagado y cambio de tarjeta. Lo que cambia entre ellas es la URL que
 * devuelve el contrato JSON de Pago_panel (`application/libraries/pagos/
 * Pago_panel.php`); esta pieza no sabe qué se está comprando.
 *
 * Métodos de pago MX (spec 2026-09-05 §5):
 *   - Express Checkout Element ARRIBA (Apple Pay, Google Pay, Link) sobre el
 *     mismo intent; abajo el Payment Element en acordeón con radios y orden
 *     fijo: tarjeta, Link, OXXO, SPEI (`paymentMethodOrder`). Los wallets se
 *     apagan en el acordeón (`wallets: never`) para no duplicar botones.
 *   - Tarjeta guardada ("Pagar $X con tu Visa •••• 4242"): confirma el mismo
 *     PaymentIntent con `confirmParams.payment_method`; "Usar otro método"
 *     abre el acordeón.
 *   - `confirmPayment({redirect: 'if_required'})`: tarjeta/Link/wallet
 *     resuelven aquí y el navegador se va a `retorno` con `redirect_status`
 *     (mismo polling al webhook de siempre). OXXO/SPEI dejan el PaymentIntent
 *     en `requires_action` con el vale/instrucciones que Stripe ya mostró en
 *     su modal: se manda la REFERENCIA a `urlPendiente`, el servidor relee el
 *     intent y persiste el vale, y esta pantalla pasa a "Pendiente de pago".
 *
 * Reglas que NO se negocian:
 *   - El total lo dice el servidor (`contrato.total`).
 *   - Un cobro asíncrono YA emitido (vale de OXXO, CLABE de SPEI) NUNCA
 *     devuelve el botón de pagar. Si el registro en el servidor falla, se
 *     reintenta y se pinta la pantalla pendiente PROVISIONAL con lo que Stripe
 *     ya enseñó: un segundo cobro solo por "Pagar con otro método", y avisando
 *     de que el vale sigue vivo (`decisionTrasPendienteFallido`).
 *   - El navegador NUNCA acredita ni cambia estatus: al volver de Stripe se
 *     hace polling al endpoint de estado hasta que el webhook acredite. Si el
 *     servidor contesta que ya sabe más que la pantalla (`YA_ACREDITADA`,
 *     `MONTO_NO_COINCIDE`), esta no afirma nada: mensaje neutro y recarga.
 *   - Contrato de 13 claves (`Pago_panel::_contrato()`): 10 de la entrega 1
 *     (`activa: true` con `intent_client_secret: null` sigue siendo "no hay
 *     nada que confirmar") + `metodos`, `tarjeta_guardada`, `pendiente`. Un
 *     contrato con `pendiente` (orden ya en 6) tampoco trae secret: se pinta
 *     la pantalla pendiente (ver `evaluarIniciar`, pura, con Vitest).
 *   - Dos contratos, no uno: `contratoVista` (lo pintado) y `contratoIntent`
 *     (lo que está detrás del Element montado). `confirmar()` SOLO lee
 *     `contratoIntent`; `cotizar()` lo invalida.
 *   - Stripe.js se carga desde https://js.stripe.com/v3/, única excepción a
 *     la regla de no-CDN (PCI). No se vendoriza.
 */
import * as totales from './totales.js';

var mx = totales.mx;

var stripe = null;
var elements = null;
var paymentElement = null;
var expressElement = null;
var contratoVista = null;
var contratoIntent = null;
var secretIntent = '';       // client secret del intent montado (tarjeta guardada / handleNextAction)
var metodoElegido = '';      // tipo seleccionado en el acordeón (card, link, oxxo, customer_balance)
var pendienteVista = null;   // bloque `pendiente` pintado ("Ver instrucciones")
var registroPendiente = null; // { referencia, bloque } del vale ya emitido que falta registrar
var extraIniciar = null;     // `extra` de la última llamada a iniciar() (para rehacer el intent, N7)
var observadorTema = null;   // MutationObserver de `data-bs-theme` y `data-fd-palette` (uno por página)
var cfg = null;

function $id(id) { return document.getElementById(id); }
function texto(id, valor) { var el = $id(id); if (el) { el.textContent = valor; } }
function mostrar(id, si) { var el = $id(id); if (el) { el.hidden = !si; } }

function pintarError(msg) {
  var box = $id('fd-pago-error');
  if (!box) { return; }
  if (!msg) { box.hidden = true; box.textContent = ''; return; }
  box.textContent = msg;
  box.hidden = false;
}

/**
 * Orden del acordeón (spec §5): tarjeta, Link, OXXO, SPEI. Puro. Solo los
 * tipos que el contrato anuncia (`metodos`, con `spei` como nombre de
 * pantalla de `customer_balance`); lo que no esté en la lista lo ignora
 * Stripe, y lo que el intent traiga de más lo pone después.
 */
export function ordenMetodos(metodos) {
  var FIJO = ['card', 'link', 'oxxo', 'customer_balance'];
  var set = {};
  (Array.isArray(metodos) ? metodos : []).forEach(function (m) {
    set[m === 'spei' ? 'customer_balance' : String(m)] = true;
  });
  return FIJO.filter(function (t) { return set[t]; });
}

/** Texto del botón para el contrato `c` y el método elegido. Puro. */
export function etiquetaBoton(c, metodo) {
  if (!c) { return 'Pagar'; }
  if (c.intent_tipo === 'setup') { return 'Guardar tarjeta'; }
  if (metodo === 'oxxo') { return 'Generar vale OXXO'; }
  if (metodo === 'customer_balance' || metodo === 'spei') { return 'Ver datos para transferir'; }
  return 'Pagar ' + mx(c.total);
}

/** Texto del botón grande de la tarjeta guardada del customer. Puro. */
export function textoTarjetaGuardada(c) {
  var tg = c && c.tarjeta_guardada;
  if (!tg || !tg.id) { return 'Pagar con tu tarjeta guardada'; }
  return 'Pagar ' + mx(c.total) + ' con tu ' + (tg.marca || 'tarjeta') + ' •••• ' + (tg.ultimos4 || '');
}

/**
 * Stripe garantiza texto presentable al usuario SOLO para `card_error` y
 * `validation_error`; en `api_error`/`invalid_request_error`/etc. el
 * `message` puede traer detalle interno (ids de objetos, nombres de
 * parámetros) que no se le enseña tal cual a un cliente.
 */
function mensajeErrorStripe(error, porDefecto) {
  if (error && (error.type === 'card_error' || error.type === 'validation_error') && error.message) {
    return error.message;
  }
  return porDefecto || MSG_PAGO_NO_COMPLETO;
}

/** El cargo no se completó (el banco o Stripe lo dijeron): nada se cobró y hay con qué seguir. */
var MSG_PAGO_NO_COMPLETO = 'Tu pago aún no se completa. Revisa los datos o elige otro método de pago.';
/** Guardar la tarjeta (SetupIntent) no cobra nada: el texto habla de la tarjeta, no del pago. */
var MSG_TARJETA_NO_GUARDADA = 'Tu tarjeta aún no se guardó. Revisa los datos o usa otra.';
/** Abrir las instrucciones de un vale u operación pendiente (handleNextAction): ahí tampoco se cobra nada. */
var MSG_INSTRUCCIONES = 'Aún no logramos abrir las instrucciones de tu pago. Recarga la página en un momento.';
/**
 * No sabemos si el cobro alcanzó a hacerse (Stripe no contestó, estado raro): ahí repetir puede duplicarlo (guía de
 * tono, regla 12). Se dice dónde revisarlo antes de volver a intentar; si el cobro ya aparece, el webhook acredita solo.
 */
var MSG_PAGO_SIN_CONFIRMAR = 'Aún no tenemos la confirmación de tu pago. Antes de volver a intentarlo, revisa tu historial en unos minutos: '
  + 'si el cobro ya aparece, tu compra se acredita sola y te avisamos por correo.';

/** Lo que se le dice a quien confirmó sobre un PaymentIntent que ya no servía (N7). */
export var MSG_PAGO_REHECHO = 'Este pago se volvió a preparar desde otra pestaña o por otro usuario de la cuenta. '
  + 'Ya lo preparamos de nuevo: revisa el total y vuelve a intentarlo.';
var MSG_PAGO_RECARGA = 'Este pago ya cambió de estado. Recarga la página y vuelve a intentarlo.';

/**
 * ¿Se puede volver a pedir el PaymentIntent de este contrato sin preguntarle
 * a nadie? Solo en COMPRAS ÚNICAS (`referencia` pi_…: timbres, créditos de IA,
 * Premium prepagado): el servidor vuelve a decidir sobre la orden y nunca abre
 * un segundo cobro. Una suscripción (sub_…) o un cambio de tarjeta (seti_…)
 * no se rehacen solos. Pura (Vitest).
 */
export function puedeReiniciar(contrato) {
  return !!(contrato && contrato.intent_tipo !== 'setup'
    && typeof contrato.referencia === 'string' && contrato.referencia.indexOf('pi_') === 0);
}

/**
 * El `extra` con el que se rehace el intent (N7): el de la última llamada a
 * `iniciar()`, SIN `otro_metodo`. Rehacer no es "pagar con otro método": si
 * mientras tanto quedó un vale o una CLABE, el servidor los enseña en vez de
 * cancelarlos. Pura (Vitest).
 */
export function extraParaReiniciar(extra) {
  var out = {};
  Object.keys(extra || {}).forEach(function (k) {
    if (k !== 'otro_metodo') { out[k] = extra[k]; }
  });
  return out;
}

/**
 * Qué hacer con lo que devolvió `stripe.confirmPayment({redirect:'if_required'})`.
 * Puro: sin DOM, sin Stripe (Vitest).
 *
 *   error        se enseña y se repone el botón.
 *   reiniciar    el PaymentIntent montado ya no se puede confirmar
 *                (`payment_intent_unexpected_state` y no cobró): se vuelve a
 *                pedir con `iniciar()` y se explica por qué.
 *   redirigido   sin `paymentIntent`: Stripe ya mandó al navegador a return_url.
 *   pendiente    OXXO/SPEI: `requires_action` con vale/instrucciones; el
 *                servidor persiste el vale (urlPendiente) y se pinta la
 *                pantalla pendiente.
 *   exito        succeeded/processing/requires_capture: se va a `retorno`
 *                con `redirect_status` y la vista espera al webhook.
 */
export function resolverConfirmacion(res) {
  if (res && res.error) {
    // El PaymentIntent ya no está en un estado que se pueda confirmar
    // (revisión 2026-09-26, ronda 4, N7). Pasa cuando OTRO usuario de la
    // cuenta, u otra pestaña, retomó la misma orden: el servidor le dio su
    // propio PaymentIntent y canceló el que esta pestaña tenía montado. Lo
    // que diga Stripe del intent manda: si ya cobró (otra pestaña pagó), es
    // éxito; si no, se vuelve a pedir el intent en vez de reponer el botón
    // sobre uno muerto.
    if (res.error.code === 'payment_intent_unexpected_state') {
      var piErr = res.error.payment_intent || null;
      var st = (piErr && piErr.status) ? String(piErr.status) : '';
      if (st === 'succeeded' || st === 'processing' || st === 'requires_capture') {
        return { tipo: 'exito', referencia: String(piErr.id || ''), estado: st };
      }
      return { tipo: 'reiniciar', msg: MSG_PAGO_REHECHO };
    }
    // Stripe no contestó (red o error suyo): no sabemos si el cobro alcanzó a hacerse. Aquí, y no en
    // mensajeErrorStripe (que también usan confirmSetup y handleNextAction), aplica la regla 12 de la guía.
    if (res.error.type === 'api_error' || res.error.type === 'api_connection_error') {
      return { tipo: 'error', msg: MSG_PAGO_SIN_CONFIRMAR };
    }
    return { tipo: 'error', msg: mensajeErrorStripe(res.error) };
  }
  var pi = res && res.paymentIntent;
  if (!pi || !pi.status) {
    return { tipo: 'redirigido' };
  }
  var na = (pi.next_action && pi.next_action.type) ? String(pi.next_action.type) : '';
  if (pi.status === 'requires_action' && (na === 'oxxo_display_details' || na === 'display_bank_transfer_instructions')) {
    return { tipo: 'pendiente', referencia: String(pi.id || ''), estado: pi.status };
  }
  if (pi.status === 'succeeded' || pi.status === 'processing' || pi.status === 'requires_capture') {
    return { tipo: 'exito', referencia: String(pi.id || ''), estado: pi.status };
  }
  if (pi.status === 'requires_payment_method') {
    return { tipo: 'error', msg: MSG_PAGO_NO_COMPLETO };
  }
  return { tipo: 'error', msg: MSG_PAGO_SIN_CONFIRMAR };
}

/**
 * Lo que Stripe ya cobró de una transferencia SPEI incompleta, en pesos, o
 * `null` si no hay fondeo parcial que anunciar. Puro.
 *
 * `restante` (`Pago_panel::pendiente_de_orden()`) es el `amount_remaining` que
 * Stripe recalcula en `payment_intent.partially_funded`, y SOLO existe en
 * SPEI: en OXXO `monto_centavos` es el importe íntegro del vale, así que
 * tratarlo como "ya llegó parte" le descontaría al cliente dinero que nunca
 * pagó mientras trae el vale completo en la mano. Si un `restante` apareciera
 * en un bloque de OXXO, se ignora.
 */
function faltaSpei(p) {
  if (!p || p.metodo !== 'spei') { return null; }
  var falta = Number(p.restante ?? null);
  var total = Number(p.monto) || 0;
  if (!isFinite(falta) || falta <= 0 || falta >= total) { return null; }
  return falta;
}

/** Copy de la pantalla "Pendiente de pago". Puro. */
export function textoPendiente(p) {
  var oxxo = !!(p && p.metodo === 'oxxo');
  var falta = faltaSpei(p);
  var total = Number(p && p.monto) || 0;
  var ref = '';
  if (oxxo) {
    ref = 'Referencia: ' + ((p && p.referencia) ? p.referencia : '');
  } else {
    ref = 'CLABE: ' + ((p && p.clabe) ? p.clabe : '')
      + ((p && p.banco) ? ' (' + p.banco + ')' : '')
      + ((p && p.referencia) ? ' · Referencia: ' + p.referencia : '');
  }
  return {
    titulo: oxxo ? 'Tu vale de OXXO está listo' : 'Tus datos para transferir están listos',
    metodo: oxxo ? 'Vale de OXXO' : 'Transferencia SPEI',
    // Con fondeo parcial se pide lo que FALTA, nunca el total: el cliente ya
    // transfirió una parte y volver a pedirle todo le cobraría de más.
    monto: mx(falta === null ? total : falta),
    parcial: (falta === null) ? '' : ('Ya recibimos ' + mx(total - falta) + '; falta ' + mx(falta)),
    vence: (p && p.vence_larga) ? ('Paga antes del ' + p.vence_larga) : 'Paga cuanto antes',
    referencia: ref,
    ver: oxxo ? 'Ver mi vale' : 'Ver datos para transferir',
    // Con dinero en camino el PaymentIntent ya no se puede cancelar
    // (`Pago_stripe::cancelar_payment_intent()` se niega con FONDEADO_PARCIAL),
    // así que ofrecer "pagar con otro método" solo llevaría a un error del
    // servidor: no se ofrece.
    otroMetodo: (falta === null),
    aviso: 'Te avisamos por correo cuando se acredite. ' + (oxxo
      ? 'Lo que compraste se activa en cuanto OXXO nos confirme el pago (hasta un día hábil).'
      : 'Lo que compraste se activa en cuanto tu banco envíe la transferencia.'),
  };
}

/**
 * Lo que hay que advertirle al cliente ANTES de abrirle un segundo cobro
 * (spec §8, carry I3 de la Tarea 3). Puro.
 *
 * OXXO: el vale YA existe en la tienda y el servidor NO lo cancela (no se
 * puede). Si el cliente lo paga después de haber pagado por otra vía, se le
 * cobra dos veces y OXXO no admite reembolso: se le dice con todas sus letras
 * y con la fecha de vigencia, y decide él.
 * SPEI: el servidor cancela la transferencia (la orden pasa a 3); basta con
 * decirlo. Salvo que ya haya llegado dinero, y entonces no hay cambio posible.
 */
export function avisoOtroMetodo(p) {
  if (faltaSpei(p) !== null) {
    return { ok: false, msg: 'Ya recibimos parte de tu transferencia, así que el método de pago se queda como está. '
      + 'Completa el resto con los mismos datos; en cuanto llegue, lo acreditamos.' };
  }
  if (p && p.metodo === 'oxxo') {
    return {
      ok: true,
      titulo: '¿Pagar con otro método?',
      texto: 'Tu vale de OXXO sigue vigente' + (p.vence_larga ? (' hasta el ' + p.vence_larga) : '')
        + '; si lo pagas, se te cobrará también. ¿Continuar?',
      boton: 'Usar otro método',
    };
  }
  if (p && p.metodo === 'spei') {
    return {
      ok: true,
      titulo: '¿Pagar con otro método?',
      texto: 'Vamos a cancelar la transferencia pendiente: la CLABE y la referencia que tienes dejarán de servir. ¿Continuar?',
      boton: 'Usar otro método',
    };
  }
  return { ok: true, titulo: '¿Pagar con otro método?', texto: 'Vamos a preparar un cobro nuevo. ¿Continuar?',
    boton: 'Usar otro método' };
}

/**
 * Qué hacer con la respuesta de `urlPendiente` (`Pago_panel::marcar_pendiente()`).
 * Puro.
 *
 * `YA_ACREDITADA` (el webhook ganó la carrera) y `MONTO_NO_COINCIDE` (el
 * servidor se negó fail-closed porque el PaymentIntent no cobra lo que vale la
 * orden) son estados que solo el servidor conoce: la pantalla no afirma ni
 * éxito ni fracaso, da un mensaje neutro y recarga a la URL limpia, donde el
 * servidor repinta la verdad.
 */
var ERR_SERVIDOR_MANDA = ['YA_ACREDITADA', 'MONTO_NO_COINCIDE', 'ORDEN_CERRADA'];
var MSG_RECARGAR = 'Ya tenemos el estado de este pago. Vamos a recargar la página para mostrarte lo último.';

export function resolverPendienteAjax(r) {
  var err = (r && r.error) ? String(r.error) : '';
  if (ERR_SERVIDOR_MANDA.indexOf(err) >= 0) {
    return { tipo: 'recargar', msg: MSG_RECARGAR };
  }
  if (!r || String(r.code) !== '200' || !r.pendiente) {
    // El `codigo` viaja para que `decisionTrasPendienteFallido()` sepa si vale
    // la pena reintentar; el `msg` es lo que ve el cliente.
    return { tipo: 'error', codigo: err, msg: (r && r.msg) ? r.msg
      : 'Tus datos de pago se generaron, pero no pudimos guardarlos. Recarga la página en un momento.' };
  }
  return { tipo: 'pendiente' };
}

// Cuántos POST a `urlPendiente` se hacen SOLOS antes de pedirle el clic al
// cliente, y la base de la espera entre ellos (2 s tras el primer fallo, 4 s
// tras el segundo). `marcar_pendiente()` es idempotente: relee el mismo
// PaymentIntent y escribe los mismos campos, así que reintentar es gratis.
var INTENTOS_PENDIENTE = 3;
var ESPERA_PENDIENTE_MS = 2000;

/**
 * Qué hacer cuando el POST a `urlPendiente` falla DESPUÉS de que Stripe ya
 * emitió el vale de OXXO / las instrucciones de SPEI. Puro.
 *
 * Es el camino peligroso de toda la pantalla: el cobro YA existe (por eso
 * `resolverConfirmacion()` dijo `pendiente`) y un vale de OXXO no se cancela
 * ni se reembolsa. Devolverle al cliente el botón de pagar —con el acordeón
 * montado y `card` a un clic— lo lleva a pagar dos veces sin que nadie se lo
 * haya advertido. Así que por aquí NO se restaura nada: se reintenta el
 * registro y mientras tanto se pinta la pantalla pendiente PROVISIONAL con lo
 * que Stripe ya le enseñó en su modal.
 *
 * Entran por aquí `ORDEN_NO_ENCONTRADA` (4040), `NO_PENDIENTE`, un 500 del
 * endpoint y la red caída; los tres errores en que el SERVIDOR sabe más
 * (`YA_ACREDITADA`, `MONTO_NO_COINCIDE`, `ORDEN_CERRADA`) no se reintentan:
 * ahí se recarga, que es lo que ya hacía `resolverPendienteAjax()`.
 *
 * @param {string} codigo    `error` del servidor ('' si fue la red o un 500 sin cuerpo)
 * @param {number} intentos  POSTs ya hechos (1 = acaba de fallar el primero)
 * @returns {{tipo:string, espera:number, msg:string}}
 *          `reintentar` con su espera · `rendirse` (botón "Reintentar registro")
 *          · `recargar` (ni se reintenta ni se pinta nada: manda el servidor)
 */
export function decisionTrasPendienteFallido(codigo, intentos) {
  if (ERR_SERVIDOR_MANDA.indexOf(String(codigo || '')) >= 0) {
    return { tipo: 'recargar', espera: 0, msg: MSG_RECARGAR };
  }
  var n = Number(intentos) || 0;
  // Un contador inválido (0, NaN) NO reintenta: sin cuenta que avance, un
  // reintento automático sería un bucle contra el servidor.
  if (n >= 1 && n < INTENTOS_PENDIENTE) {
    return { tipo: 'reintentar', espera: ESPERA_PENDIENTE_MS * n, msg: '' };
  }
  return { tipo: 'rendirse', espera: 0, msg: '' };
}

var MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

/**
 * "9 de septiembre de 2026" a partir de un epoch en SEGUNDOS (el
 * `expires_after` del vale). Puro. Gemela de `Cuenta_seccion::fecha_larga()`.
 *
 * Se lee con el reloj de la Ciudad de México (UTC-6 todo el año: México dejó
 * el horario de verano en 2022), que es el del servidor. Con el del navegador,
 * un cliente en otro huso vería un día distinto del que el servidor le va a
 * pintar en cuanto recargue.
 */
export function fechaLargaEpoch(seg) {
  var n = Number(seg);
  if (!isFinite(n) || n <= 0) { return ''; }
  var d = new Date((n - 6 * 3600) * 1000);
  return d.getUTCDate() + ' de ' + MESES[d.getUTCMonth()] + ' de ' + d.getUTCFullYear();
}

/**
 * El bloque `pendiente` PROVISIONAL a partir del PaymentIntent que devolvió
 * `confirmPayment`: el mismo `next_action` que Stripe le acaba de enseñar al
 * cliente en su modal. Puro. Gemela de
 * `Pago_stripe::pendiente_de_payment_intent()` + `Pago_panel::pendiente_de_orden()`.
 *
 * Es SOLO para pintar mientras el servidor registra el vale: el navegador no
 * acredita ni guarda nada, y en cuanto `marcar_pendiente()` conteste manda el
 * contrato del servidor.
 */
export function pendienteDeNextAction(pi) {
  var na = (pi && pi.next_action) ? pi.next_action : null;
  if (!na) { return null; }
  var tipo = String(na.type || '');
  var total = Math.round(Number(pi.amount) || 0) / 100;
  if (tipo === 'oxxo_display_details') {
    var d = na.oxxo_display_details || {};
    return {
      metodo: 'oxxo',
      monto: total,
      vence: '',
      vence_larga: fechaLargaEpoch(d.expires_after),
      url: String(d.hosted_voucher_url || ''),
      referencia: String(d.number || ''),
      clabe: '',
      banco: '',
    };
  }
  if (tipo === 'display_bank_transfer_instructions') {
    var b = na.display_bank_transfer_instructions || {};
    var dir = (Array.isArray(b.financial_addresses) ? b.financial_addresses : []).filter(function (fa) {
      return !!(fa && fa.spei && fa.spei.clabe);
    })[0];
    var spei = (dir && dir.spei) ? dir.spei : {};
    var bloque = {
      metodo: 'spei',
      monto: total,
      // La vigencia de SPEI no viene en el `next_action`: la pone el servidor
      // (FD_SPEI_VIGENCIA_DIAS). Vacía, la pantalla dice "Paga cuanto antes"
      // en vez de inventar una fecha con el reloj del navegador.
      vence: '',
      vence_larga: '',
      url: String(b.hosted_instructions_url || ''),
      referencia: String(b.reference || ''),
      clabe: String(spei.clabe || ''),
      banco: String(spei.bank_name || ''),
    };
    // `restante` (lo que FALTA) solo si Stripe ya recibió parte: en un cobro
    // recién emitido `amount_remaining` es el total y no hay nada que anunciar.
    var falta = Math.round(Number(b.amount_remaining) || 0) / 100;
    if (falta > 0 && falta < total) { bloque.restante = falta; }
    return bloque;
  }
  return null;
}

/**
 * El aviso de la pantalla pendiente PROVISIONAL. Puro. Dice las dos cosas que
 * importan: el vale existe (no lo tire) y qué hacer si no aparece.
 */
export function textoProvisional(p) {
  var cosa = (p && p.metodo === 'oxxo') ? 'tu vale' : 'tu transferencia';
  return 'Estamos registrando ' + cosa + '; si no aparece en tu historial en unos minutos, escríbenos a soporte@facturadigital.com.mx.';
}

/**
 * La URL hospedada por Stripe si se puede abrir en otra pestaña, o ''. Puro.
 *
 * Hoy el dato viene de Stripe y se persiste verbatim, así que no hay por dónde
 * colar un `javascript:`; es la única URL de la pantalla que se abría sin
 * mirar y no cuesta nada mirarla.
 */
export function urlSegura(url) {
  var s = String(url || '');
  return /^https:\/\//i.test(s) ? s : '';
}

/**
 * Mezcla el contrato pendiente sobre el que estaba vivo. Puro.
 *
 * `_contrato_pendiente()` viaja con `retorno: ''` (no tiene URL de vuelta que
 * dar); una fusión a lo bruto borraría el `retorno` del contrato que sí lo
 * trae y dejaría a la pantalla sin a dónde volver.
 */
export function fusionarPendiente(previo, r) {
  var p = previo || {};
  var out = {};
  Object.keys(p).forEach(function (k) { out[k] = p[k]; });
  Object.keys(r || {}).forEach(function (k) { out[k] = r[k]; });
  if (!out.retorno && p.retorno) { out.retorno = p.retorno; }
  return out;
}

/**
 * ¿Hay al menos un botón exprés que pintar? Puro.
 *
 * Stripe cambió la forma del dato: el `ready` viejo trae
 * `availablePaymentMethods` con booleanos y el evento nuevo
 * (`availablepaymentmethodschange`, que además vuelve a disparar si cambian)
 * trae `paymentMethods` con objetos `{available}`. Leer el nuevo con la
 * lógica del viejo enseñaría el divisor "o paga con" sobre una fila vacía.
 */
export function hayWallets(disponibles) {
  if (!disponibles || typeof disponibles !== 'object') { return false; }
  return Object.keys(disponibles).some(function (k) {
    var v = disponibles[k];
    return (v && typeof v === 'object') ? !!v.available : !!v;
  });
}

/**
 * En qué estado va el contenedor del Express Checkout Element. Puro.
 *
 * Stripe necesita ese contenedor EN EL FLUJO y con ancho para renderizar y
 * medir sus botones (`layout.maxColumns` depende del ancho del contenedor):
 * montado dentro de un `display:none` —que es lo que hace el atributo `hidden`
 * con el reboot de Bootstrap— la fila de Apple Pay / Google Pay / Link sale a
 * 0 px o vacía, sin un solo error en consola, y con ella se va el camino de
 * mayor conversión. Los docs de Stripe esconden este bloque con `visibility`
 * mientras tanto, así que aquí hay TRES estados y no dos:
 *
 *   midiendo  todavía no se sabe: en el flujo, con ancho, invisible y sin alto.
 *   visible   hay al menos un wallet: se enseña con su alto (y con el divisor).
 *   vacio     no hay ninguno (o no hay Element montado): fuera del flujo.
 *
 * @param {Object} disponibles   `availablePaymentMethods` (del `ready`, booleanos)
 *                               o `paymentMethods` ({available}) del evento nuevo.
 * @param {boolean} autoritativo `true` solo para `availablepaymentmethodschange`:
 *                               el campo del `ready` está deprecado y solo puede
 *                               ENCENDER (un `ready` tardío con el campo ya
 *                               retirado escondería botones vivos).
 */
export function estadoExpress(disponibles, autoritativo) {
  if (hayWallets(disponibles)) { return 'visible'; }
  return autoritativo ? 'vacio' : 'midiendo';
}

/** `retorno` del contrato + `redirect_status`, como lo haría Stripe al redirigir. Puro. */
export function urlRetorno(retorno, estado) {
  var base = String(retorno || '');
  var sep = base.indexOf('?') >= 0 ? '&' : '?';
  return base + sep + 'redirect_status=' + encodeURIComponent(estado || 'succeeded');
}

/** La misma pantalla sin los parámetros del cobro: a dónde se recarga. Puro. */
export function urlLimpia(url) {
  var s = String(url || '');
  var i = s.indexOf('?');
  var base = (i >= 0) ? s.slice(0, i) : s;
  var h = base.indexOf('#');
  return (h >= 0) ? base.slice(0, h) : base;
}

/** Repone el botón de pago a su estado clicable con la etiqueta de `c`. */
function restaurarBoton(c) {
  var btn = $id('fd-pago-btn');
  if (btn) { btn.disabled = false; btn.textContent = etiquetaBoton(c, metodoElegido); }
  var tg = $id('fd-tarjeta-guardada-btn');
  if (tg) { tg.disabled = false; tg.textContent = textoTarjetaGuardada(c); }
}

/**
 * Deja los dos botones de cobro apagados y sin spinner. Es el único final para
 * un cobro que ya está vivo y no se puede repetir: ni "Procesando…" para
 * siempre ni un botón clicable que cobraría dos veces.
 */
function bloquearCobro() {
  ['fd-pago-btn', 'fd-tarjeta-guardada-btn'].forEach(function (id) {
    var b = $id(id);
    if (b) { b.disabled = true; b.textContent = 'Pago en curso'; }
  });
}

/** Deshabilita los dos botones de cobro y pone el spinner. */
function ocupado() {
  ['fd-pago-btn', 'fd-tarjeta-guardada-btn'].forEach(function (id) {
    var b = $id(id);
    if (b) {
      b.disabled = true;
      b.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Un momento…';
    }
  });
}

function pintarDesglose(c) {
  var cont = $id('fd-pago-desglose');
  if (!cont) { return; }
  var filas = (c.desglose || []).map(function (f) {
    var neg = Number(f.monto) < 0;
    return '<div class="fd-pago-linea' + (neg ? ' is-desc' : '') + '">' +
      '<span></span><span class="num">' + mx(f.monto) + '</span></div>';
  }).join('');
  cont.innerHTML = filas;
  // El texto de cada etiqueta se asigna con textContent: puede venir de la
  // descripción de un módulo o del código de un cupón que escribió el usuario.
  var spans = cont.querySelectorAll('.fd-pago-linea > span:first-child');
  (c.desglose || []).forEach(function (f, i) {
    if (spans[i]) { spans[i].textContent = f.etiqueta; }
  });

  var total = $id('fd-pago-total');
  if (total) { total.textContent = mx(c.total); }
  var btn = $id('fd-pago-btn');
  if (btn) { btn.textContent = etiquetaBoton(c, metodoElegido); }
}

/** Desmonta los Elements vigentes (si los hay) y limpia el estado de Stripe.js. */
function desmontarElement() {
  if (paymentElement) {
    try { paymentElement.unmount(); } catch (e) { /* el nodo ya no existe */ }
  }
  if (expressElement) {
    try { expressElement.unmount(); } catch (e) { /* idem */ }
  }
  paymentElement = null;
  expressElement = null;
  elements = null;
  secretIntent = '';
  metodoElegido = '';
  // Sin Element montado el bloque exprés se retira del flujo: no hay nada que
  // Stripe tenga que medir ahí.
  pintarExpress('vacio');
  mostrar('fd-tarjeta-guardada', false);
}

/** Vuelve a enseñar el formulario de cobro (tras un pendiente o una tarjeta guardada). */
function mostrarFormulario(si) {
  mostrar('fd-payment-element', si);
  mostrar('fd-pago-btn', si);
  mostrar('fd-pago-seguro', si);
}

/**
 * La tarjeta guardada del customer (`contrato.tarjeta_guardada`) en compras:
 * el botón grande cobra con ella y esconde el acordeón hasta que el usuario
 * pida "Usar otro método". En un SetupIntent no aplica (se está cambiando).
 */
function pintarTarjetaGuardada(c) {
  var caja = $id('fd-tarjeta-guardada');
  if (!caja) { return; }
  var tg = c && c.tarjeta_guardada;
  if (!tg || !tg.id || c.intent_tipo === 'setup') { caja.hidden = true; return; }
  var b = $id('fd-tarjeta-guardada-btn');
  if (b) { b.disabled = false; b.textContent = textoTarjetaGuardada(c); }
  caja.hidden = false;
  mostrar('fd-payment-element', false);
  mostrar('fd-pago-btn', false);
}

function usarOtroMetodo() {
  mostrar('fd-tarjeta-guardada', false);
  mostrar('fd-payment-element', true);
  mostrar('fd-pago-btn', true);
  pintarError('');
}

/**
 * Aplica el estado de `estadoExpress()` al contenedor exprés y a su divisor
 * "o paga con". Nunca por `hidden`: las clases las define `_cuenta.scss`
 * (`.is-midiendo` invisible pero en el flujo, `.is-vacio` fuera del flujo).
 */
function pintarExpress(estado) {
  var caja = $id('fd-express-element');
  if (caja && caja.classList) {
    caja.classList.toggle('is-midiendo', estado === 'midiendo');
    caja.classList.toggle('is-vacio', estado === 'vacio');
  }
  mostrar('fd-pago-o', estado === 'visible');
}

/**
 * Variables del tema oscuro para Stripe: [variable de Stripe, token del tema,
 * respaldo]. El respaldo es el valor del token en `_tokens.scss`
 * (`[data-bs-theme="dark"]`, paleta "libro").
 */
var TOKENS_OSCURO = [
  ['colorPrimary', '--fd-primary', '#60a5fa'],
  ['colorBackground', '--fd-surface-1', '#1e293b'],
  ['colorText', '--fd-ink-1', '#e2e8f0'],
  ['colorDanger', '--fd-danger', '#f87171'],
];

/**
 * Las de fábrica del tema 'stripe' para las variables que el oscuro sí fija
 * (tabla de variables de la Appearance API). Solo viajan en una
 * ACTUALIZACIÓN de oscuro a claro (ver `aparienciaStripe`).
 */
var CLARO_DE_FABRICA = { colorBackground: '#ffffff', colorText: '#30313d', colorDanger: '#df1b41' };

/**
 * Un color que se le puede pasar a Stripe en `colorPrimary`,
 * `colorBackground`, `colorText` o `colorDanger`, o '' si no lo es. Solo hex
 * OPACO de 3 o 6 dígitos: esas variables no admiten transparencia (rgba,
 * hsla, hex de 4 u 8 dígitos) y una sintaxis que Stripe no espera llegaría
 * tal cual al montaje del Element. Todo lo demás —un token vacío, sin
 * resolver (`var(--x)`), rgb(), hsl() o un nombre de color— cae al respaldo
 * de `TOKENS_OSCURO`. Pura (Vitest).
 */
export function colorStripe(v) {
  var s = String(v === null || v === undefined ? '' : v).trim();
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(s) ? s : '';
}

/** Paletas de Apariencia B cuyo acento no es el azul de Libro Mayor (Atardecer: la tinta). */
var PALETAS_CON_ACENTO = ['marca', 'grafito', 'bosque', 'atardecer'];

/**
 * La `appearance` de los Elements de Stripe según el tema y la paleta de la
 * página. Pura (Vitest).
 *
 *   CLARO con Libro Mayor (o sin paleta): EXACTAMENTE la de siempre, tema
 *   'stripe' con el azul de la marca (#1D4ED8); no se lee ningún token.
 *   CLARO con Marca, Grafito, Bosque o Atardecer (visto bueno de Adrián, 26-sep:
 *   Mi Cuenta sigue la paleta también en claro): la misma, con `colorPrimary` =
 *   el acento de la paleta (`--fd-primary`, leído en vivo; en Atardecer, la
 *   tinta #1b1a17); si el token no es un hex opaco, el azul de siempre.
 *   OSCURO: tema 'night' con los colores del tema (`--fd-*`), leídos en vivo
 *   porque la paleta cambia el primario. Un token vacío o en un formato que
 *   Stripe no acepta cae a su respaldo. El Express Checkout toma de aquí el
 *   tema de sus botones (Stripe lo deriva de `appearance`).
 *
 * `completa` es para `elements.update()`: Stripe no documenta si ahí las
 * variables se reemplazan o se FUSIONAN con las anteriores. Con `completa`,
 * el claro trae además, explícitas, las de fábrica del tema 'stripe' para
 * cada variable que el oscuro fija: volver de oscuro a claro queda igual que
 * hoy en cualquiera de los dos casos.
 *
 * @param {boolean} oscuro
 * @param {function(string):string} [leerToken] valor computado de una custom property
 * @param {boolean} [completa]
 * @param {string} [paleta] `data-fd-palette` de <html> ('libro', 'marca'…)
 */
export function aparienciaStripe(oscuro, leerToken, completa, paleta) {
  if (!oscuro) {
    var claro = { colorPrimary: '#1D4ED8', fontFamily: 'Inter, sans-serif' };
    if (PALETAS_CON_ACENTO.indexOf(paleta) !== -1) {
      var acento = '';
      try { acento = (typeof leerToken === 'function') ? colorStripe(leerToken('--fd-primary')) : ''; } catch (e) { acento = ''; }
      if (acento) { claro.colorPrimary = acento; }
    }
    if (completa) {
      Object.keys(CLARO_DE_FABRICA).forEach(function (k) { claro[k] = CLARO_DE_FABRICA[k]; });
    }
    return { theme: 'stripe', variables: claro };
  }
  var variables = {};
  TOKENS_OSCURO.forEach(function (t) {
    var v = '';
    try { v = (typeof leerToken === 'function') ? colorStripe(leerToken(t[1])) : ''; } catch (e) { v = ''; }
    variables[t[0]] = v || t[2];
  });
  variables.fontFamily = 'Inter, sans-serif';
  return { theme: 'night', variables: variables };
}

/** ¿La página está en oscuro? El atributo que fija `head.php` en <html>. Pura (Vitest, con un doc falso). */
export function documentoOscuro(doc) {
  var h = doc && doc.documentElement;
  return !!(h && typeof h.getAttribute === 'function' && h.getAttribute('data-bs-theme') === 'dark');
}

/** La paleta de <html> (`data-fd-palette`, Apariencia B) o ''. Pura (Vitest). */
export function paletaDocumento(doc) {
  var h = doc && doc.documentElement;
  return (h && typeof h.getAttribute === 'function') ? String(h.getAttribute('data-fd-palette') || '') : '';
}

/**
 * Sigue al tema en vivo y llama `alCambiar(oscuro, paleta)` cuando cambia lo
 * que la apariencia de Stripe lee: `data-bs-theme` de <html> pasa de claro a
 * oscuro o al revés (el panel de Apariencia, o el tema del sistema), o cambia
 * la paleta (`data-fd-palette`: el primario de `--fd-primary` es otro, en
 * oscuro y también en claro). Devuelve el observador, o null si no hay con qué
 * observar. `Observador` es el constructor de MutationObserver (inyectable
 * para Vitest).
 */
export function observarTema(doc, Observador, alCambiar) {
  if (!doc || !doc.documentElement || typeof Observador !== 'function' || typeof alCambiar !== 'function') {
    return null;
  }
  var previo = { oscuro: documentoOscuro(doc), paleta: paletaDocumento(doc) };
  var obs = new Observador(function () {
    var ahora = { oscuro: documentoOscuro(doc), paleta: paletaDocumento(doc) };
    var cambio = (ahora.oscuro !== previo.oscuro) || (ahora.paleta !== previo.paleta);
    previo = ahora;
    if (cambio) { alCambiar(ahora.oscuro, ahora.paleta); }
  });
  obs.observe(doc.documentElement, { attributes: true, attributeFilter: ['data-bs-theme', 'data-fd-palette'] });
  return obs;
}

function leerTokenDocumento(nombre) {
  return window.getComputedStyle(document.documentElement).getPropertyValue(nombre);
}

/**
 * Le pasa a los Elements montados la apariencia del tema y la paleta de ahora
 * (`elements.update`, completa), sin desmontarlos. Nada del cobro depende de
 * esto: si falla, se queda la apariencia anterior. Pura salvo por `elements`
 * y `leerToken` (Vitest, con dobles).
 */
export function aplicarAparienciaStripe(els, oscuro, paleta, leerToken) {
  if (!els || typeof els.update !== 'function') { return; }
  try {
    var p = els.update({ appearance: aparienciaStripe(oscuro, leerToken, true, paleta) });
    if (p && typeof p.catch === 'function') { p.catch(function () { /* se queda la apariencia anterior */ }); }
  } catch (e) { /* se queda la apariencia anterior: nada del cobro depende de esto */ }
}

/**
 * Un solo observador por página: al cambiar el tema se le pasa la apariencia
 * nueva a los Elements montados (`elements.update`), sin desmontarlos (el
 * usuario no pierde lo que ya escribió). Los que se monten después nacen con
 * la del momento.
 */
function seguirTema() {
  if (observadorTema || typeof window === 'undefined' || typeof window.MutationObserver !== 'function') { return; }
  observadorTema = observarTema(document, window.MutationObserver, function (oscuro, paleta) {
    aplicarAparienciaStripe(elements, oscuro, paleta, leerTokenDocumento);
  });
}

function montarElement(secret, c) {
  if (!window.Stripe || !cfg.pk) {
    pintarError('El pago no está disponible en este momento.');
    var b = $id('fd-pago-btn');
    if (b) { b.disabled = true; }
    return false;
  }
  desmontarElement();
  mostrar('fd-pago-pendiente', false);
  mostrarFormulario(true);
  // Una sola instancia de Stripe.js por página (lo recomienda Stripe) y con
  // locale explícito: sin `locale: 'es'` los mensajes de error de Stripe
  // salen en el idioma del navegador, no necesariamente español.
  if (!stripe) {
    stripe = window.Stripe(cfg.pk, { locale: 'es' });
  }
  secretIntent = secret;
  elements = stripe.elements({
    clientSecret: secret,
    appearance: aparienciaStripe(documentoOscuro(document), leerTokenDocumento, false, paletaDocumento(document)),
  });
  seguirTema();
  var esPago = (c.intent_tipo !== 'setup');

  // Express Checkout (Apple Pay, Google Pay, Link) arriba, solo en cobros.
  // Se enseña únicamente si Stripe reporta al menos un wallet disponible en
  // este navegador; si no, ni el divisor. Se escuchan los DOS eventos: el
  // `ready` (campo deprecado, booleanos) y `availablepaymentmethodschange`
  // (objetos `{available}`, y vuelve a disparar si la disponibilidad cambia).
  var cajaExpress = $id('fd-express-element');
  if (cajaExpress && esPago) {
    // EN EL FLUJO antes de montar: Stripe mide el ancho del contenedor para
    // decidir cuántos botones caben. Invisible, pero medible.
    pintarExpress('midiendo');
    expressElement = elements.create('expressCheckout', {
      buttonType: { applePay: 'buy', googlePay: 'buy' },
      buttonHeight: 44,
      layout: { maxColumns: 3, maxRows: 1 },
      paymentMethods: { applePay: 'auto', googlePay: 'auto', link: 'auto', paypal: 'never', klarna: 'never', amazonPay: 'never' },
      paymentMethodOrder: ['applePay', 'googlePay', 'link'],
    });
    // `availablepaymentmethodschange` es el AUTORITATIVO (dispara también en
    // el render inicial y puede apagar); el campo del `ready` está deprecado y
    // solo se usa para ENCENDER, por si esta versión de Stripe.js todavía no
    // emitiera el evento nuevo. Al revés —dejando que el `ready` apagara— un
    // `ready` tardío con el campo ya retirado escondería botones vivos.
    expressElement.on('ready', function (ev) {
      // Solo ENCIENDE: si dijera `midiendo` volvería a esconder unos botones
      // que el evento autoritativo ya hubiera encendido.
      if (estadoExpress(ev && ev.availablePaymentMethods, false) === 'visible') { pintarExpress('visible'); }
    });
    expressElement.on('availablepaymentmethodschange', function (ev) {
      pintarExpress(estadoExpress(ev && ev.paymentMethods, true));
    });
    // Hay 1 segundo para llamar `resolve()` o la hoja del wallet no abre. Sin
    // payload a propósito: todo lo que se le pasaba aquí (emailRequired,
    // business, billingAddressRequired) está deprecado en favor de las
    // opciones de `create`, y la dirección de facturación se deja en su
    // default (`true`), que es lo que Stripe recomienda contra el fraude.
    expressElement.on('click', function (ev) { ev.resolve(); });
    expressElement.on('confirm', function (ev) { confirmarCon({ elements: elements }, ev); });
    expressElement.mount('#fd-express-element');
  }

  paymentElement = elements.create('payment', {
    layout: { type: 'accordion', defaultCollapsed: false, radios: 'always', spacedAccordionItems: true },
    paymentMethodOrder: ordenMetodos(c.metodos),
    wallets: { applePay: 'never', googlePay: 'never' },
    business: { name: 'FacturaDigital' },
  });
  paymentElement.on('change', function (ev) {
    metodoElegido = (ev && ev.value && ev.value.type) ? String(ev.value.type) : '';
    var btn = $id('fd-pago-btn');
    if (btn && !btn.disabled) { btn.textContent = etiquetaBoton(contratoIntent, metodoElegido); }
    pintarError('');
  });
  paymentElement.mount('#fd-payment-element');

  var btn = $id('fd-pago-btn');
  if (btn) { btn.disabled = false; btn.hidden = false; btn.textContent = etiquetaBoton(c, ''); }
  pintarTarjetaGuardada(c);
  return true;
}

/**
 * Pide una cotización (sin crear nada) y repinta el resumen del cargo.
 * Cualquier cotización nueva INVALIDA los Elements vigentes: el total que se
 * acaba de pintar ya no es el que Stripe va a cobrar. Quien quiera cobrar el
 * total nuevo tiene que volver a llamar `iniciar()`.
 */
function cotizar(extra) {
  return window.FD.ajax({
    method: 'POST',
    url: cfg.urlCotizar,
    data: window.jQuery.extend({}, cfg.datos, extra || {}),
  }).done(function (r) {
    if (!r || String(r.code) !== '200') {
      pintarError((r && r.msg) ? r.msg : 'Aún no logramos calcular el total. Inténtalo de nuevo en un momento.');
      return;
    }
    pintarError('');
    contratoVista = r;
    pintarDesglose(r);
    desmontarElement();
    contratoIntent = null;
    var btn = $id('fd-pago-btn');
    if (btn) { btn.disabled = true; }
    if (typeof cfg.alCotizar === 'function') { cfg.alCotizar(r); }
  }).fail(function () {
    pintarError('Aún no logramos calcular el total. Revisa tu conexión e inténtalo de nuevo.');
  });
}

/**
 * Interpreta el contrato de `urlIniciar`. Pura: sin DOM, sin red (Vitest).
 *
 * Orden de las preguntas, y por qué:
 *   1. `code` distinto de 200: error de negocio, con su `msg`.
 *   2. `pendiente`: la orden YA está en 6 (vale/CLABE emitidos): no hay
 *      secret ni nada que confirmar; se pinta la pantalla pendiente.
 *   3. `activa`: cupón del 100 %, la factura nace pagada; `intent_client_secret`
 *      viaja null a propósito.
 *   4. Si no, hace falta el secret.
 */
export function evaluarIniciar(r) {
  if (!r || String(r.code) !== '200') {
    return { ok: false, msg: (r && r.msg) ? r.msg : 'Tu pago aún no se inicia. Inténtalo de nuevo en un momento.' };
  }
  if (r.pendiente && typeof r.pendiente === 'object') {
    return { ok: true, pendiente: r.pendiente };
  }
  // Comparación ESTRICTA a propósito: `Pago_panel::_contrato()` hace `(bool)`
  // sobre `activa`, así que el JSON siempre trae `true`/`false` reales.
  if (r.activa === true) {
    return { ok: true, activa: true };
  }
  if (!r.intent_client_secret) {
    return { ok: false, msg: (r && r.msg) ? r.msg : 'Tu pago aún no se inicia. Inténtalo de nuevo en un momento.' };
  }
  return { ok: true, activa: false, secret: r.intent_client_secret };
}

/** Pide el intent real y monta los Elements (o pinta pendiente / se salta todo si `activa`). */
function iniciar(extra) {
  extraIniciar = extra || null;
  var btn = $id('fd-pago-btn');
  if (btn) { btn.disabled = true; }
  return window.FD.ajax({
    method: 'POST',
    url: cfg.urlIniciar,
    data: window.jQuery.extend({}, cfg.datos, extra || {}),
  }).done(function (r) {
    var v = evaluarIniciar(r);
    if (!v.ok) {
      pintarError(v.msg);
      desmontarElement();
      contratoIntent = null;
      return;
    }
    pintarError('');
    contratoVista = r;
    pintarDesglose(r);
    if (v.pendiente) {
      // La orden ya tiene vale/CLABE (el usuario volvió, o repitió el clic):
      // no se monta nada, se enseña dónde y cuánto pagar.
      desmontarElement();
      contratoIntent = r;
      pintarPendiente(r);
      if (typeof cfg.alPendiente === 'function') { cfg.alPendiente(r); }
      return;
    }
    if (v.activa) {
      desmontarElement();
      contratoIntent = null;
      if (btn) { btn.hidden = true; }
      mostrar('fd-payment-element', false);
      if (typeof cfg.alActivar === 'function') { cfg.alActivar(r); }
      return;
    }
    contratoIntent = r;
    montarElement(v.secret, r);
  }).fail(function () {
    pintarError('Tu pago aún no se inicia. Inténtalo de nuevo en un momento.');
    desmontarElement();
    contratoIntent = null;
  });
}

/**
 * Decide si se puede llamar a Stripe. Pura (Vitest). `contratoIntent` es el
 * de la ÚLTIMA llamada a `iniciar()` que montó Elements (`null` si una
 * cotización posterior lo invalidó); sin `retorno` Stripe rechazaría el
 * confirm por `return_url` vacía.
 */
export function puedeConfirmar(contratoIntent, elementMontado) {
  if (!elementMontado || !contratoIntent) {
    return { ok: false, msg: 'El pago con tarjeta no está disponible en este momento.' };
  }
  if (!contratoIntent.retorno) {
    return { ok: false, msg: 'Tu pago aún no está listo. Vuelve a intentarlo en un momento.' };
  }
  return { ok: true };
}

/**
 * Confirma un COBRO con `redirect: 'if_required'`. `base` trae `elements`
 * (acordeón o Express Checkout) o `clientSecret` + `confirmParams.payment_method`
 * (tarjeta guardada). `ev` es el evento `confirm` del Express Checkout, si
 * vino de ahí: un error se le devuelve con `paymentFailed` para que la hoja
 * del wallet lo enseñe.
 */
function confirmarCon(base, ev) {
  var v = puedeConfirmar(contratoIntent, !!(stripe && (elements || base.clientSecret)));
  if (!v.ok) {
    pintarError(v.msg);
    if (ev && typeof ev.paymentFailed === 'function') { ev.paymentFailed({ reason: 'fail' }); }
    return;
  }
  ocupado();
  var opciones = { confirmParams: { return_url: contratoIntent.retorno }, redirect: 'if_required' };
  if (base.elements) { opciones.elements = base.elements; }
  if (base.clientSecret) { opciones.clientSecret = base.clientSecret; }
  if (base.confirmParams) {
    Object.keys(base.confirmParams).forEach(function (k) { opciones.confirmParams[k] = base.confirmParams[k]; });
  }
  stripe.confirmPayment(opciones).then(function (res) {
    manejarResultado(res, ev);
  }).catch(function () {
    // confirmPayment RECHAZA (no resuelve con {error}) ante un IntegrationError
    // o la red caída a medio confirmar. Sin este catch el botón se queda en
    // "Procesando…" para siempre.
    if (ev && typeof ev.paymentFailed === 'function') { ev.paymentFailed({ reason: 'fail' }); }
    pintarError(MSG_PAGO_SIN_CONFIRMAR);
    restaurarBoton(contratoIntent);
  });
}

function manejarResultado(res, ev) {
  var r = resolverConfirmacion(res);
  if (r.tipo === 'error') {
    if (ev && typeof ev.paymentFailed === 'function') { ev.paymentFailed({ reason: 'fail' }); }
    pintarError(r.msg);
    restaurarBoton(contratoIntent);
    return;
  }
  if (r.tipo === 'reiniciar') {
    if (ev && typeof ev.paymentFailed === 'function') { ev.paymentFailed({ reason: 'fail' }); }
    if (!puedeReiniciar(contratoIntent)) {
      // Suscripción o cambio de tarjeta: no se rehacen solos; recargar vuelve
      // a pedirle al servidor lo que toca.
      pintarError(MSG_PAGO_RECARGA);
      restaurarBoton(contratoIntent);
      return;
    }
    // El servidor vuelve a decidir sobre la orden (su intent, o el vale que
    // haya quedado) y la pantalla se repinta con lo que conteste.
    desmontarElement();
    contratoIntent = null;
    var aviso = r.msg;
    iniciar(extraParaReiniciar(extraIniciar)).done(function (rr) {
      if (evaluarIniciar(rr).ok) { pintarError(aviso); }
    });
    return;
  }
  if (r.tipo === 'redirigido') {
    return;   // Stripe ya mandó al navegador a return_url
  }
  if (r.tipo === 'exito') {
    // Mismo camino que el retorno de Stripe: la vista hace polling al webhook.
    window.location.href = urlRetorno(contratoIntent.retorno, r.estado);
    return;
  }
  // El vale / la CLABE YA existen: a partir de aquí el botón de pagar no vuelve
  // pase lo que pase (ver `tropiezoPendiente`). El bloque provisional sale del
  // mismo `next_action` que Stripe le acaba de enseñar en su modal.
  registroPendiente = { referencia: r.referencia, bloque: pendienteDeNextAction(res && res.paymentIntent) };
  registrarPendiente(0);
}

/** Mensaje neutro y recarga a la URL limpia: el servidor sabe más que la pantalla. */
function recargarLimpio(msg) {
  pintarError(msg);
  var destino = urlLimpia(contratoIntent && contratoIntent.retorno)
    || urlLimpia(window.location.href);
  window.setTimeout(function () { window.location.href = destino; }, 2500);
}

/**
 * OXXO/SPEI: Stripe ya enseñó el vale/instrucciones en su modal. El servidor
 * relee el PaymentIntent, persiste el vale y pasa la orden a 6; aquí solo se
 * manda la referencia (`registroPendiente`, que se llenó en `manejarResultado`).
 *
 * @param {number} intentos POSTs ya hechos antes de este (0 = el primero)
 */
function registrarPendiente(intentos) {
  var reg = registroPendiente || { referencia: '', bloque: null };
  var n = (Number(intentos) || 0) + 1;
  if (!cfg.urlPendiente) {
    // Sin endpoint no hay nada que registrar (ni por qué reintentar): el vale
    // existe y el webhook dejará la orden en 6. Se enseña lo que ya se sabe.
    pintarProvisional(false);
    return;
  }
  window.FD.ajax({
    method: 'POST',
    url: cfg.urlPendiente,
    data: window.jQuery.extend({}, cfg.datos, { referencia: reg.referencia }),
  }).done(function (r) {
    var v = resolverPendienteAjax(r);
    if (v.tipo === 'recargar') { recargarLimpio(v.msg); return; }
    if (v.tipo === 'error') { tropiezoPendiente(v.codigo, n); return; }
    registroPendiente = null;
    contratoIntent = fusionarPendiente(contratoIntent, r);
    pintarPendiente(r);
    if (typeof cfg.alPendiente === 'function') { cfg.alPendiente(r); }
  }).fail(function () {
    tropiezoPendiente('', n);
  });
}

/**
 * El registro del vale falló y el vale YA EXISTE. Aquí NO se restaura el botón
 * de pagar (sería ofrecerle un segundo cobro sin advertirle del primero, y el
 * de OXXO no se puede cancelar): se pinta la pantalla pendiente provisional
 * con lo que Stripe ya le enseñó y se reintenta el registro.
 *
 * El `msg` del servidor NO se pinta a propósito: "No encontramos esa compra"
 * debajo de "Tu vale de OXXO está listo" solo asusta a quien tiene el vale en
 * la mano. Lo que el cliente necesita saber lo dice `textoProvisional()`, y el
 * detalle del error vive en el log del servidor.
 */
function tropiezoPendiente(codigo, intentos) {
  var d = decisionTrasPendienteFallido(codigo, intentos);
  if (d.tipo === 'recargar') { recargarLimpio(d.msg); return; }
  pintarProvisional(d.tipo === 'rendirse');
  if (d.tipo === 'reintentar') {
    window.setTimeout(function () { registrarPendiente(intentos); }, d.espera);
  }
}

/**
 * La pantalla pendiente PROVISIONAL: los datos son los del `next_action` que
 * Stripe ya le enseñó al cliente, y el aviso dice que el registro va en
 * camino. "Pagar con otro método" sigue ahí con la MISMA confirmación de doble
 * cobro que en la pantalla pendiente de siempre (`otroMetodoPendiente`).
 *
 * Lo que NO se hace es llamar a `alPendiente()`: la vista (historial, tarjetas
 * de estado) solo debe moverse cuando el SERVIDOR confirme que la orden quedó
 * en 6. Esta pantalla es del navegador y se cambia sola en cuanto el registro
 * cuaje o el cliente recargue.
 */
function pintarProvisional(conBotonReintentar) {
  var bloque = registroPendiente && registroPendiente.bloque;
  if (!bloque) {
    // No se pudo leer el `next_action` (no debería pasar: es el mismo que hizo
    // que `resolverConfirmacion()` dijera `pendiente`). Sin pantalla que
    // pintar, lo único que NO se hace es devolver el botón de cobro.
    bloquearCobro();
    pintarError('Tu pago ya está en curso. Recarga la página en un momento para ver tus datos de pago.');
    return;
  }
  pintarPendiente({ pendiente: bloque }, { msg: textoProvisional(bloque), reintentar: !!conBotonReintentar });
}

/** "Reintentar registro": el cliente pide otra tanda de intentos. */
function reintentarRegistro() {
  if (!registroPendiente) { return; }
  mostrar('fd-pendiente-reintentar', false);
  registrarPendiente(0);
}

/**
 * Pinta el bloque "Pendiente de pago" con el contrato `r` (que trae `pendiente`).
 *
 * @param {Object} r
 * @param {{msg:string, reintentar:boolean}} [provisional] cuando los datos son
 *        los del `next_action` y el servidor todavía no registró el vale: se
 *        añade el aviso y, si ya no quedan reintentos, el botón de reintentar.
 */
function pintarPendiente(r, provisional) {
  var p = r && r.pendiente;
  if (!p) { return; }
  pendienteVista = p;
  var prov = provisional || null;
  var t = textoPendiente(p);
  texto('fd-pendiente-titulo', t.titulo);
  texto('fd-pendiente-metodo', t.metodo);
  texto('fd-pendiente-monto', t.monto);
  texto('fd-pendiente-vence', t.vence);
  texto('fd-pendiente-ref', t.referencia);
  texto('fd-pendiente-aviso', t.aviso);
  texto('fd-pendiente-parcial', t.parcial);
  mostrar('fd-pendiente-parcial', t.parcial !== '');
  mostrar('fd-pendiente-otro', t.otroMetodo);
  texto('fd-pendiente-provisional', prov ? prov.msg : '');
  mostrar('fd-pendiente-provisional', !!prov);
  mostrar('fd-pendiente-reintentar', !!(prov && prov.reintentar));
  // El botón lleva ícono: se reescribe solo su etiqueta, no el botón entero.
  var ver = $id('fd-pendiente-ver');
  if (ver) {
    var etiqueta = ver.querySelector('.fd-btn-t');
    if (etiqueta) { etiqueta.textContent = t.ver; } else { ver.textContent = t.ver; }
  }
  desmontarElement();
  mostrarFormulario(false);
  pintarError('');
  mostrar('fd-pago-pendiente', true);
  var panel = $id('fd-panel-pago');
  if (panel) { panel.hidden = false; }
}

/**
 * Reabre el vale de OXXO / las instrucciones de SPEI. Primero la página
 * hospedada por Stripe (siempre existe y sirve aunque el PaymentIntent ya
 * no esté montado); si no hubiera URL, `handleNextAction` sobre el intent
 * vigente (solo mientras siga en requires_action).
 */
function verInstrucciones() {
  var p = pendienteVista;
  var url = urlSegura(p && p.url);
  if (url) {
    window.open(url, '_blank', 'noopener');
    return;
  }
  if (stripe && secretIntent) {
    stripe.handleNextAction({ clientSecret: secretIntent }).then(function (res) {
      if (res && res.error) { pintarError(mensajeErrorStripe(res.error, MSG_INSTRUCCIONES)); }
    }).catch(function () {
      pintarError(MSG_INSTRUCCIONES);
    });
    return;
  }
  pintarError(MSG_INSTRUCCIONES);
}

/**
 * "Pagar con otro método" sobre una orden pendiente. En OXXO deja DOS cobros
 * vivos por diseño (el vale no se cancela), así que se pide confirmación
 * explícita antes de avisarle a la vista.
 */
function otroMetodoPendiente() {
  var a = avisoOtroMetodo(pendienteVista);
  if (!a.ok) { pintarError(a.msg); return; }
  var seguir = function () {
    pintarError('');
    if (typeof cfg.alOtroMetodo === 'function') { cfg.alOtroMetodo(contratoIntent); }
  };
  if (window.Swal && typeof window.Swal.fire === 'function') {
    window.Swal.fire({
      icon: 'question', title: a.titulo, text: a.texto,
      showCancelButton: true, confirmButtonText: a.boton, cancelButtonText: 'Ahora no',
    }).then(function (res) {
      if (res && res.isConfirmed) { seguir(); }
    });
    return;
  }
  // Sin SweetAlert cargado NO se sigue de largo: el aviso del vale vivo es la
  // única defensa contra el doble cobro de OXXO.
  if (window.confirm(a.texto)) { seguir(); }
}

/** Botón principal: `confirmSetup` para tarjeta (redirige siempre), `confirmarCon` para cobro. */
function confirmar() {
  var v = puedeConfirmar(contratoIntent, !!(stripe && elements));
  if (!v.ok) {
    pintarError(v.msg);
    return;
  }
  if (contratoIntent.intent_tipo === 'setup') {
    ocupado();
    stripe.confirmSetup({ elements: elements, confirmParams: { return_url: contratoIntent.retorno } }).then(function (res) {
      if (res && res.error) {
        pintarError(mensajeErrorStripe(res.error, MSG_TARJETA_NO_GUARDADA));
        restaurarBoton(contratoIntent);
      }
      // Sin error el navegador se va a `return_url`.
    }).catch(function () {
      pintarError(MSG_TARJETA_NO_GUARDADA);
      restaurarBoton(contratoIntent);
    });
    return;
  }
  confirmarCon({ elements: elements }, null);
}

/** "Pagar $X con tu Visa •••• 4242": mismo PaymentIntent, payment_method guardado. */
function confirmarConGuardada() {
  var tg = contratoIntent && contratoIntent.tarjeta_guardada;
  if (!tg || !tg.id || !secretIntent) {
    pintarError('Aún no logramos usar tu tarjeta guardada; elige otro método de pago.');
    usarOtroMetodo();
    return;
  }
  confirmarCon({ clientSecret: secretIntent, confirmParams: { payment_method: tg.id } }, null);
}

/**
 * Espera a que el WEBHOOK acredite: "Confirmando tu pago…" cada 2 s hasta
 * 30 s. Quien la llama revisa `redirect_status` ANTES (con `failed` no hay
 * nada que esperar).
 *
 * @param {string} referencia  pi_… / sub_… / seti_… que devolvió el contrato
 * @param {function(boolean, Object=)} alTerminar
 */
function esperarAcreditacion(referencia, alTerminar) {
  var intentos = 0;
  var MAX = 15;   // 15 × 2 s = 30 s
  (function reintentar() {
    intentos++;
    window.FD.ajax({
      method: 'POST',
      url: cfg.urlEstado,
      data: { referencia: referencia },
    }).done(function (r) {
      if (r && String(r.code) === '200' && r.acreditado) {
        alTerminar(true, r);
        return;
      }
      if (intentos >= MAX) { alTerminar(false, r); return; }
      window.setTimeout(reintentar, 2000);
    }).fail(function () {
      if (intentos >= MAX) { alTerminar(false, null); return; }
      window.setTimeout(reintentar, 2000);
    });
  })();
}

/**
 * @param {Object} opciones
 * @param {string} opciones.pk             publishable key de Stripe
 * @param {string} opciones.urlCotizar     endpoint que devuelve el contrato sin crear nada
 * @param {string} opciones.urlIniciar     endpoint que crea el intent
 * @param {string} opciones.urlEstado      endpoint de polling post-retorno
 * @param {string} [opciones.urlPendiente] endpoint que persiste el vale/CLABE (POST referencia)
 * @param {Object} [opciones.datos]        datos fijos que van en cada POST
 * @param {function} [opciones.alCotizar]   tras cada `cotizar()` exitoso
 * @param {function} [opciones.alActivar]   `iniciar()` resolvió con `activa: true`
 * @param {function} [opciones.alPendiente] la orden quedó/está pendiente (contrato con `pendiente`)
 * @param {function} [opciones.alOtroMetodo] el usuario pulsó "Pagar con otro método" y CONFIRMÓ
 */
function montar(opciones) {
  cfg = window.jQuery.extend({ datos: {} }, opciones || {});
  // Idempotente: una segunda llamada (o una vista que la repita) no puede
  // dejar Elements de Stripe colgando sobre nodos que ya se van a repintar.
  desmontarElement();
  contratoVista = null;
  contratoIntent = null;
  pendienteVista = null;
  registroPendiente = null;
  // Un remonte tampoco puede dejar de pie la pantalla pendiente de la vez
  // anterior: "Ver mi vale" y "Pagar con otro método" quedarían encima de un
  // `pendienteVista` que ya no existe.
  mostrar('fd-pago-pendiente', false);
  mostrar('fd-pendiente-provisional', false);
  mostrar('fd-pendiente-reintentar', false);
  var bind = function (id, fn) {
    var el = $id(id);
    // Guard contra doble-bind: sin esto cada clic dispararía dos confirmaciones.
    if (el && !el.dataset.fdPagoBound) {
      el.addEventListener('click', fn);
      el.dataset.fdPagoBound = '1';
    }
  };
  bind('fd-pago-btn', confirmar);
  bind('fd-tarjeta-guardada-btn', confirmarConGuardada);
  bind('fd-tarjeta-otro', usarOtroMetodo);
  bind('fd-pendiente-ver', verInstrucciones);
  bind('fd-pendiente-otro', otroMetodoPendiente);
  bind('fd-pendiente-reintentar', reintentarRegistro);
  return {
    cotizar: cotizar, iniciar: iniciar, confirmar: confirmar, esperarAcreditacion: esperarAcreditacion,
    pintarPendiente: pintarPendiente, verInstrucciones: verInstrucciones,
  };
}

// Registro global: SOLO en el navegador (bajo Vitest no hay `window`).
if (typeof window !== 'undefined') {
  window.FD = window.FD || {};
  window.FD.pago = { montar: montar };
  // Aritmética pura disponible en las vistas (no son módulos ES).
  window.FDCuenta = totales;
}

export { montar, cotizar, iniciar, confirmar, esperarAcreditacion };
